import React from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '@/app/store';

import {
  actOnDeleted,
  conflictIsSelfContained,
  DeletedAction,
  DeletedKind,
  listDeleted,
  Tombstone,
} from '@/features/deleted/deletedApi';
import { deletedScopeKey, deletedSlice, selectDeletedScope } from '@/features/deleted/deletedSlice';

import { getUnixEUTime } from '@/features/projects/projectsSlice';

import Button from '@/components/Common/Button';
import FlexRow from '@/components/Common/FlexRow';
import { ButtonsModal } from '@/components/Common/Modals';

export interface DeletedPanelProps {
  kind:				DeletedKind;
  hotelId?:			string;		// required for kind=room; selects local users for kind=user
  title:			string;
  subtitle?:		string;
  icon?:			string;		// material icon for the card badge
  canView:			boolean;
  canAct?:			boolean;	// defaults to canView — same privilege gates both
  defaultOpen?:		boolean;
  eager?:			boolean;
  refreshSignal?:	number;
  onChanged?:		() => void;	// re-fetch the live grid after a restore
}

interface RowState {
  busy:				DeletedAction | "";
  error:			string;
  conflict:			boolean;
}

const emptyRowState: RowState = { busy: "", error: "", conflict: false };

const th = 'text-left px-[16px] py-[11px] text-[11px] font-semibold uppercase ' +
           'tracking-[0.7px] text-[#8A93A3] whitespace-nowrap';
const td = 'px-[16px] py-[13px] align-middle';

const DeletedPanel = (props: DeletedPanelProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const canAct = props.canAct === undefined ? props.canView : props.canAct;

  const scopeUsable = props.canView && (props.kind !== "room" || !!props.hotelId);

  const scopeKey = deletedScopeKey(props.kind, props.hotelId);
  const scope = useAppSelector((state) => selectDeletedScope(state.deleted, props.kind, props.hotelId));

  const [open, setOpen] = React.useState(!!props.defaultOpen);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [notice, setNotice] = React.useState("");
  const [forbidden, setForbidden] = React.useState(false);
  const [rowState, setRowState] = React.useState<{ [id: string]: RowState }>({});
  const [toPurge, setToPurge] = React.useState<Tombstone | null>(null);
  const [refreshTick, setRefreshTick] = React.useState(0);

  const refreshSignal = props.refreshSignal;
  const external = refreshSignal !== undefined;

  React.useEffect(() => {
    setRowState({});
    setError("");
    setNotice("");
    setForbidden(false);
  }, [scopeKey]);

  React.useEffect(() => {
    if (	(!open && !props.eager) ||
			!scopeUsable
	   )
      return;

    let cancelled = false;

    setLoading(true);
    setError("");

    listDeleted(props.kind, props.hotelId)
      .then((res) => {
        if (cancelled)
          return;

        switch (res.kind) {
        case "ok":
          setForbidden(false);
          dispatch(deletedSlice.actions.setItems({ key: scopeKey, items: res.items }));
          break;

        case "forbidden":
          setForbidden(true);
          break;

        case "unauthorized":
          setError(t('deleted:error:unauthorized'));
          break;

        default:
          setError(res.message || t('deleted:error:generic'));
          break;
        }
      })
      .finally(() => {
        if (!cancelled)
          setLoading(false);
      });

    return () => { cancelled = true; };
  }, [open, props.eager, scopeUsable, scopeKey, props.kind, props.hotelId,
      refreshTick, refreshSignal, dispatch, t]);

  const setRow = React.useCallback((id: string, next: Partial<RowState>) => {
    setRowState((prev) => ({
      ...prev,
      [id]: { ...emptyRowState, ...prev[id], ...next },
    }));
  }, []);

  const runAction = React.useCallback(async (item: Tombstone, action: DeletedAction) => {
    setRow(item.id, { busy: action, error: "", conflict: false });

    const hotel =	props.kind === "room"
					?	(props.hotelId || item.hotel)
					:	undefined;

    const res = await actOnDeleted(props.kind, item.id, action, hotel);

    switch (res.kind) {
    case "ok":
      dispatch(deletedSlice.actions.dropItem({ key: scopeKey, id: item.id }));
      setRow(item.id, { busy: "" });
      // MW light (#91, §2.7): a restored project comes back with an EMPTY MW
      // light list — the server does not resurrect it. Say so here, or the
      // housekeeping staff silently lose their access and nobody knows why.
      if (	action === "restore" &&
			props.kind === "project"
		 )
        setNotice(t('deleted:restored_mw_light'));
      if (	action === "restore" &&
			props.onChanged
		 )
        props.onChanged();
      break;

    case "conflict":
      setRow(item.id, {
        busy:		"",
        conflict:	true,
        error:		conflictIsSelfContained(res.message)
					?	res.message
					:	t('deleted:error:conflict', { m: res.message }),
      });
      break;

    case "gone":
      dispatch(deletedSlice.actions.dropItem({ key: scopeKey, id: item.id }));
      setRow(item.id, { busy: "" });
      setNotice(res.message || t('deleted:error:gone'));
      setRefreshTick((n) => n + 1);
      break;

    case "forbidden":
      setRow(item.id, { busy: "", error: res.message || t('deleted:error:forbidden') });
      break;

    case "unauthorized":
      setRow(item.id, { busy: "", error: t('deleted:error:unauthorized') });
      break;

    default:
      setRow(item.id, { busy: "", error: res.message || t('deleted:error:generic') });
      break;
    }
  }, [dispatch, props, scopeKey, setRow, t]);

  const handlePurge = (_obj: object, result: string) => {
    const item = toPurge;

    setToPurge(null);

    if (	result === "yes" &&
			item
	   )
      runAction(item, "purge");
  };

  // Nothing to show and nothing to ask for: a user who cannot view this kind
  // never sees the section, and never fires the request behind it.
  if (!props.canView)
    return null;

  const items = scope.items;
  const counted = scope.loaded && !forbidden;

  return (
  <FlexRow direction="column"
           align="start"
           className="w-full rounded-[12px] border-solid border-1 border-[#E6E9EF] bg-white overflow-hidden">
    <FlexRow className={cx(
               'px-[20px] py-[16px] gap-[14px] cursor-pointer select-none',
               open ? 'border-b-1 border-solid border-[#EEF0F4]' : '',
             )}
             onClick={() => setOpen(!open)}>
      <div className="w-[40px] h-[40px] rounded-[10px] bg-[#EEF2F8] flex items-center justify-center shrink-0">
        <span className="material-icons-outlined !text-[20px] !text-[#5A7BA6]">
          { props.icon || 'delete_outline' }
        </span>
      </div>
      <FlexRow direction="column" align="start" flex={1} className="gap-[2px]">
        <span className="text-[15px] font-semibold text-[#1F2733] leading-[20px]">
          { props.title }
        </span>
        { props.subtitle &&
          <span className="text-[13px] text-[#6B7280] leading-[18px]">
            { props.subtitle }
          </span>
        }
      </FlexRow>
      {/* Filled when there is something to recover, quiet when there isn't. */}
      { counted &&
        <span className={cx(
                'min-w-[28px] h-[28px] px-[8px] rounded-full shrink-0',
                'flex items-center justify-center text-[13px] font-semibold',
                items.length > 0
                  ? 'bg-[#3A5A8C] text-white'
                  : 'bg-[#EEF0F4] text-[#8A93A3]',
              )}>
          { items.length }
        </span>
      }
      <span className="material-icons !text-[22px] !text-[#8A93A3] shrink-0">
        { open ? 'expand_less' : 'expand_more' }
      </span>
    </FlexRow>

    { open &&
    <FlexRow direction="column" align="start" className="w-full">
      { (forbidden || error !== "" || notice !== "" || loading || items.length === 0) &&
      <FlexRow direction="column" align="start" className="px-[20px] py-[16px] gap-[12px]">
        { forbidden &&
          <span className="text-[13px] text-[#6B7280]">
            { t('deleted:forbidden') }
          </span>
        }

        { !forbidden && error !== "" &&
          <FlexRow align="start"
                   className="gap-[8px] w-full rounded-[8px] p-[10px] bg-[#FDECEA] border-solid border-1 border-[#F3C9C4]">
            <span className="material-icons !text-[16px] !text-[#B14545] shrink-0">warning_amber</span>
            <span className="text-[12px] leading-[17px] text-[#B14545] flex-1">{ error }</span>
          </FlexRow>
        }

        { !forbidden && notice !== "" &&
          <FlexRow align="start"
                   className="gap-[8px] w-full rounded-[8px] p-[10px] bg-[#F7F9FC] border-solid border-1 border-[#E6E9EF]">
            <span className="material-icons !text-[16px] !text-[#6B7280] shrink-0">info</span>
            <span className="text-[12px] leading-[17px] text-[#5A6474] flex-1">{ notice }</span>
          </FlexRow>
        }

        { !forbidden && loading && !scope.loaded &&
          <span className="text-[13px] text-[#6B7280]">{ t('deleted:loading') }</span>
        }

        { !forbidden && scope.loaded && items.length === 0 &&
          <span className="text-[13px] text-[#6B7280]">{ t('deleted:empty') }</span>
        }
      </FlexRow>
      }

      { !forbidden && items.length > 0 &&
      <div className="w-full overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-[#F7F9FC] border-b-1 border-solid border-[#EEF0F4]">
              <th className={th}>{ t('deleted:grid:identifier') }</th>
              <th className={th}>{ t('deleted:grid:name') }</th>
              <th className={th}>{ t('deleted:grid:deleted_by') }</th>
              <th className={th}>{ t('deleted:grid:deleted_on') }</th>
              <th className={th}>{ t('deleted:grid:kept_until') }</th>
              { canAct &&
                <th className={cx(th, 'text-right')}>{ t('deleted:grid:options') }</th>
              }
            </tr>
          </thead>
          <tbody>
            { items.map((item: Tombstone) => {
              const row = rowState[item.id] || emptyRowState;

              return (
              <React.Fragment key={item.id}>
                <tr className="border-b-1 border-solid border-[#F1F3F6]">
                  <td className={cx(td, 'text-[14px] font-semibold text-[#1F2733] whitespace-nowrap')}>
                    { item.key }
                  </td>
                  <td className={td}>
                    { item.name !== ""
                      ? <span className="inline-block rounded-[6px] px-[8px] py-[3px] bg-[#EEF0F4] text-[13px] text-[#5A6474]">
                          { item.name }
                        </span>
                      : <span className="text-[13px] text-[#B9C0CC]">—</span>
                    }
                  </td>
                  <td className={cx(td, 'text-[14px] font-medium text-[#1F2733] whitespace-nowrap')}>
                    { item.deleted_by || <span className="text-[#B9C0CC]">—</span> }
                  </td>
                  <td className={cx(td, 'text-[14px] text-[#6B7280] whitespace-nowrap')}>
                    { getUnixEUTime(item.deleted_at) }
                  </td>
                  <td className={td}>
                    {/* retain_until 0 means auto-purge is disabled — it is NOT
                        1 Jan 1970, so it gets a different pill entirely. */}
                    { item.retain_until > 0
                      ? <span className="inline-flex items-center gap-[6px] rounded-full px-[10px] py-[4px] bg-[#FDF4E3] text-[#8A6A2F] text-[13px] font-medium whitespace-nowrap">
                          <span className="material-icons-outlined !text-[14px]">schedule</span>
                          { getUnixEUTime(item.retain_until) }
                        </span>
                      : <span className="inline-flex items-center gap-[6px] rounded-full px-[10px] py-[4px] bg-[#EEF2F8] text-[#3A5A8C] text-[13px] font-medium whitespace-nowrap">
                          <span className="material-icons-outlined !text-[14px]">all_inclusive</span>
                          { t('deleted:retain_forever') }
                        </span>
                    }
                  </td>
                  { canAct &&
                  <td className={cx(td, 'text-right whitespace-nowrap')}>
                    <FlexRow justify="end" className="gap-[8px]">
                      <Button caption={t('deleted:restore')}
                              icon="restore"
                              layout="white"
                              isDisabled={row.busy !== ""}
                              onClick={() => runAction(item, "restore")} />
                      {/* Purge is irreversible and cascades — it always routes
                          through the confirmation rather than firing here. */}
                      <Button caption={t('deleted:purge')}
                              icon="delete"
                              layout="warning"
                              isDisabled={row.busy !== ""}
                              onClick={() => setToPurge(item)} />
                    </FlexRow>
                  </td>
                  }
                </tr>

                {/* A 409 belongs against the row that caused it, not in a
                    toast — the server's text is what tells the user to do. */}
                { row.error !== "" &&
                <tr>
                  <td colSpan={canAct ? 6 : 5} className="px-[16px] pb-[12px]">
                    <FlexRow align="start"
                             className={cx(
                               'gap-[8px] w-full rounded-[8px] p-[10px] border-solid border-1',
                               row.conflict
                                 ? 'bg-[#FDF4E3] border-[#EBD9AF]'
                                 : 'bg-[#FDECEA] border-[#F3C9C4]',
                             )}>
                      <span className={cx(
                              'material-icons !text-[16px] shrink-0',
                              row.conflict ? '!text-[#8A6A2F]' : '!text-[#B14545]',
                            )}>
                        { row.conflict ? 'error_outline' : 'warning_amber' }
                      </span>
                      <span className={cx(
                              'text-[12px] leading-[17px] flex-1',
                              row.conflict ? 'text-[#8A6A2F]' : 'text-[#B14545]',
                            )}>
                        { row.error }
                      </span>
                    </FlexRow>
                  </td>
                </tr>
                }
              </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      }

      {/* Only when the parent hasn't taken the refresh affordance over. */}
      { !forbidden && !external &&
        <FlexRow className="px-[20px] py-[12px]">
          <span className={cx(
                  'text-[13px] font-medium select-none',
                  loading ? 'text-[#B9C0CC] cursor-default' : 'text-[--Primetime-main] cursor-pointer',
                )}
                onClick={() => {
                  if (loading)
                    return;

                  setNotice("");
                  setRefreshTick(refreshTick + 1);
                }}>
            { t('deleted:refresh') }
          </span>
        </FlexRow>
      }
    </FlexRow>
    }

    { toPurge &&
      <ButtonsModal title={t('deleted:purge_confirm:caption')}
                    message={t('deleted:purge_confirm:message', {
                      k: toPurge.key || toPurge.name,
                    })}
                    buttons={[
                      { value: "no",  caption: t('modals:buttons:cancel'), layout: "white" },
                      { value: "yes", caption: t('deleted:purge'),         layout: "warning_bg" },
                    ]}
                    onResult={handlePurge}>
        <></>
      </ButtonsModal>
    }
  </FlexRow>
  );
};

export default DeletedPanel;
